import {use, useEffect, useLayoutEffect, useState} from 'react'

import {ExperimentScopeContext, ExperimentStoreContext} from './experiment-context'
import type {ExperimentScope} from './internal'

const useCommitEffect = typeof document === 'undefined' ? useEffect : useLayoutEffect

export function useNavigationScopeTransaction(
  destinationKey: string,
  resolveCandidate?: () => ExperimentScope | undefined,
): ExperimentScope {
  const store = use(ExperimentStoreContext)
  const documentScope = use(ExperimentScopeContext)
  if (!store || !documentScope) {
    throw new Error('useNavigationScopeTransaction must be used within ExperimentDocumentProvider')
  }

  const [committedDestinationKey, setCommittedDestinationKey] = useState(destinationKey)
  const isNewDestination = destinationKey !== committedDestinationKey
  const candidate = isNewDestination ? resolveCandidate?.() : undefined
  const scope = isNewDestination ? (candidate ?? documentScope) : documentScope

  useCommitEffect(() => {
    if (!isNewDestination) return
    if (candidate?.status === 'available') {
      store.startNavigation(destinationKey)
      store.captureNavigationCandidate(destinationKey, candidate)
      store.commitNavigation(destinationKey)
    }
    setCommittedDestinationKey(destinationKey)
  }, [candidate, destinationKey, isNewDestination, store])

  return scope
}
